import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Bot, AlertTriangle, ShieldCheck, FileText, Mail, CheckCircle, XCircle } from 'lucide-react';

export default function TransparenciaIAPage() {
  return (
    <div className="min-h-screen bg-slate-50" data-testid="transparencia-ia-page">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <ArrowLeft size={18} className="text-slate-500" />
            <span className="text-sm text-slate-600">Volver a inicio</span>
          </Link>
          <Link to="/">
            <img 
              src="https://customer-assets.emergentagent.com/job_baja-laboral-riesgos/artifacts/m7tg5pgh_logo.jpg" 
              alt="PrevenciónLegal @tecnoabogado" 
              className="h-10 w-auto"
            />
          </Link>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-16">
        {/* Title */}
        <div className="flex items-center gap-3 mb-4">
          <Bot className="text-rose-600" size={32} />
          <h1 className="text-3xl font-bold text-slate-900">Transparencia IA (EU AI Act)</h1>
        </div>
        <p className="text-sm text-slate-500 mb-4">
          © 2026 Evolución de la IA. Todos los derechos reservados.
        </p>
        <p className="text-xs text-slate-400 mb-8">
          Cumplimiento: GDPR, EU AI Act (Art. 50), AEPD España
        </p>

        <div className="prose prose-slate max-w-none space-y-10">

          {/* Aviso obligatorio */}
          <section className="bg-amber-50 border-l-4 border-amber-500 p-6" data-testid="aviso-obligatorio">
            <div className="flex items-start gap-3">
              <AlertTriangle className="text-amber-600 flex-shrink-0 mt-1" size={24} />
              <div>
                <h2 className="text-xl font-bold text-amber-900 mb-2">Aviso Obligatorio (EU AI Act Art. 50)</h2>
                <p className="text-amber-800">
                  Esta plataforma utiliza sistemas de inteligencia artificial para generar contenido y responder preguntas. 
                  Según el Reglamento (UE) 2024/1689 (EU AI Act), debemos informarte claramente sobre el uso de IA y sus limitaciones.
                </p>
              </div>
            </div>
          </section>

          {/* 1. Sistemas de IA */}
          <section>
            <h2 className="text-xl font-bold text-slate-900 mb-4">1. Sistemas de IA Utilizados</h2>
            <p className="text-slate-600 mb-4">La plataforma utiliza los siguientes modelos de IA:</p>
            <div className="bg-white border border-slate-200 p-6 mb-4">
              <ul className="space-y-3 text-slate-600">
                <li className="flex items-start gap-2">
                  <Bot size={18} className="text-rose-600 flex-shrink-0 mt-0.5" />
                  <span><strong>GPT-4o-mini (OpenAI):</strong> Modelo de lenguaje principal para el chatbot de prevención de riesgos laborales.</span>
                </li>
              </ul>
            </div>
            <p className="text-sm text-slate-500 bg-slate-100 p-3 border border-slate-200">
              <strong>Clasificación de riesgo:</strong> Riesgo Medio según EU AI Act (Art. 50) — Chatbot conversacional educativo
            </p>
          </section>

          {/* 2. Capacidades y Limitaciones */}
          <section>
            <h2 className="text-xl font-bold text-slate-900 mb-4">2. Capacidades y Limitaciones</h2>
            
            <div className="grid md:grid-cols-2 gap-6">
              {/* Puede hacer */}
              <div className="bg-green-50 border border-green-200 p-6">
                <h3 className="font-bold text-green-900 mb-3 flex items-center gap-2">
                  <CheckCircle size={18} />
                  Puede hacer
                </h3>
                <ul className="space-y-2 text-sm text-green-800">
                  <li>Responder preguntas sobre prevención de riesgos laborales y reincorporaciones</li>
                  <li>Explicar la Ley 2/2025, LISOS y normativa asociada</li>
                  <li>Proporcionar ejemplos y orientaciones generales</li>
                  <li>Recomendar consultar con un/a abogado/a especializado/a</li>
                  <li>Mantener contexto de la conversación</li>
                </ul>
              </div>

              {/* NO puede hacer */}
              <div className="bg-red-50 border border-red-200 p-6">
                <h3 className="font-bold text-red-900 mb-3 flex items-center gap-2">
                  <XCircle size={18} />
                  NO puede hacer
                </h3>
                <ul className="space-y-2 text-sm text-red-800">
                  <li>Proporcionar asesoramiento legal, financiero o médico profesional</li>
                  <li>Garantizar la exactitud del 100% de la información</li>
                  <li>Acceder a información en tiempo real</li>
                  <li>Tomar decisiones críticas por ti</li>
                  <li>Reemplazar la consulta con un/a abogado/a experto/a</li>
                </ul>
              </div>
            </div>
          </section>

          {/* 3. Riesgos y Consideraciones */}
          <section>
            <h2 className="text-xl font-bold text-slate-900 mb-4">3. Riesgos y Consideraciones</h2>
            
            <div className="space-y-6">
              <div className="bg-white border border-slate-200 p-6">
                <h3 className="font-bold text-slate-900 mb-2">Inexactitudes ("Alucinaciones")</h3>
                <p className="text-slate-600 text-sm">
                  Los modelos de IA pueden generar información que suena convincente pero es incorrecta o inventada. 
                  Esto se conoce como "alucinaciones". <strong>Siempre verifica información crítica con fuentes confiables.</strong>
                </p>
              </div>

              <div className="bg-white border border-slate-200 p-6">
                <h3 className="font-bold text-slate-900 mb-2">Sesgo Potencial</h3>
                <p className="text-slate-600 text-sm">
                  Los modelos IA se entrenan con datos que pueden contener sesgos (género, edad, etnia, etc.). 
                  Realizamos monitoreo mensual de sesgo según Arts. 10, 26, 29 del EU AI Act, pero no podemos garantizar ausencia total de sesgo.
                </p>
              </div>

              <div className="bg-white border border-slate-200 p-6">
                <h3 className="font-bold text-slate-900 mb-2">Información Desactualizada</h3>
                <p className="text-slate-600 text-sm">
                  Los modelos tienen un "corte de conocimiento" y no conocen eventos posteriores a su fecha de entrenamiento.
                </p>
              </div>

              <div className="bg-white border border-slate-200 p-6">
                <h3 className="font-bold text-slate-900 mb-2">Privacidad</h3>
                <p className="text-slate-600 text-sm">
                  Tus conversaciones se procesan por OpenAI (proveedor externo). 
                  Lee nuestra <Link to="/legal/privacidad" className="text-rose-600 hover:underline">Política de Privacidad</Link> para más detalles.
                </p>
              </div>
            </div>
          </section>

          {/* 4. Uso Responsable */}
          <section>
            <h2 className="text-xl font-bold text-slate-900 mb-4">4. Uso Responsable</h2>
            <p className="text-slate-600 mb-4">Para usar el chatbot de forma responsable:</p>
            <div className="bg-slate-50 border border-slate-200 p-6">
              <ul className="space-y-3 text-slate-600">
                <li className="flex items-start gap-2">
                  <span className="text-rose-600 font-bold">1.</span>
                  <span><strong>Verifica información crítica:</strong> Consulta fuentes oficiales o un/a abogado/a</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-600 font-bold">2.</span>
                  <span><strong>No compartas información sensible:</strong> Evita datos personales, contraseñas, información médica</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-600 font-bold">3.</span>
                  <span><strong>Usa con sentido crítico:</strong> No aceptes respuestas sin cuestionarlas</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-600 font-bold">4.</span>
                  <span><strong>Reporta problemas:</strong> Si detectas sesgo o información incorrecta, repórtalo</span>
                </li>
              </ul>
            </div>
          </section>

          {/* 5. Cumplimiento Legal */}
          <section>
            <h2 className="text-xl font-bold text-slate-900 mb-4">5. Cumplimiento Legal</h2>
            
            <div className="space-y-6">
              <div className="bg-white border border-slate-200 p-6">
                <h3 className="font-bold text-slate-900 mb-3 flex items-center gap-2">
                  <ShieldCheck size={18} className="text-rose-600" />
                  EU AI Act (Reglamento (UE) 2024/1689)
                </h3>
                <p className="text-slate-600 text-sm mb-3">Cumplimos con:</p>
                <ul className="space-y-2 text-sm text-slate-600">
                  <li><strong>Art. 50 — Transparencia:</strong> Informamos que el chatbot es IA</li>
                  <li><strong>Arts. 10, 26, 29 — Sesgo:</strong> Monitoreo mensual de sesgo</li>
                  <li><strong>Arts. 11, 13 — Documentación:</strong> Documentamos modelo y capacidades</li>
                </ul>
              </div>

              <div className="bg-white border border-slate-200 p-6">
                <h3 className="font-bold text-slate-900 mb-3 flex items-center gap-2">
                  <ShieldCheck size={18} className="text-rose-600" />
                  GDPR (Reglamento (UE) 2016/679)
                </h3>
                <ul className="space-y-2 text-sm text-slate-600">
                  <li>Consentimiento granular para procesamiento de datos</li>
                  <li>Derecho al olvido (auto-eliminación 90 días)</li>
                  <li>Logs auditables de interacciones IA</li>
                </ul>
              </div>

              <div className="bg-white border border-slate-200 p-6">
                <h3 className="font-bold text-slate-900 mb-3 flex items-center gap-2">
                  <ShieldCheck size={18} className="text-rose-600" />
                  AEPD (España)
                </h3>
                <p className="text-slate-600 text-sm">
                  Registrados como responsable de tratamiento ante la Agencia Española de Protección de Datos.
                </p>
              </div>
            </div>
          </section>

          {/* 6. Metadata Machine-Readable */}
          <section>
            <h2 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-2">
              <FileText size={20} />
              6. Metadata Machine-Readable
            </h2>
            <p className="text-slate-600 mb-4">Para cumplir con EU AI Act Art. 50, incluimos metadata machine-readable en el HTML:</p>
            <div className="bg-slate-900 text-slate-300 p-6 font-mono text-xs overflow-x-auto">
              <pre>{`<meta name="ai-generated-content" content="true">
<meta name="ai-model" content="GPT-4o-mini">
<meta name="ai-provider" content="OpenAI">
<meta name="disclosure-required" content="true">
<meta name="risk-level" content="medium">`}</pre>
            </div>
          </section>

          {/* 7. Reportar Problemas */}
          <section>
            <h2 className="text-xl font-bold text-slate-900 mb-4">7. Reportar Problemas</h2>
            <p className="text-slate-600 mb-4">Si encuentras:</p>
            <ul className="list-disc pl-6 space-y-2 text-slate-600 mb-6">
              <li>Información incorrecta o peligrosa</li>
              <li>Sesgo o discriminación</li>
              <li>Contenido inapropiado</li>
              <li>Violación de privacidad</li>
            </ul>
            <p className="text-slate-600">
              Por favor, repórtalo a: <a href="mailto:info@jurisciberia.com" className="text-rose-600 hover:underline font-semibold">info@jurisciberia.com</a>
            </p>
          </section>

          {/* 8. Actualizaciones */}
          <section>
            <h2 className="text-xl font-bold text-slate-900 mb-4">8. Actualizaciones</h2>
            <p className="text-slate-600 mb-4">
              Este aviso se actualiza periódicamente. <strong>Última actualización: 23 de enero de 2026.</strong>
            </p>
            <p className="text-slate-600">
              Para más información, consulta nuestros{' '}
              <Link to="/legal/aviso-legal" className="text-rose-600 hover:underline">Términos de Uso</Link>
              {' '}y{' '}
              <Link to="/legal/privacidad" className="text-rose-600 hover:underline">Política de Privacidad</Link>.
            </p>
          </section>

          {/* Contact box */}
          <section className="bg-slate-100 p-6 border border-slate-200">
            <h2 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-2">
              <Mail size={20} />
              Contacto
            </h2>
            <p className="text-slate-600 mb-4">Para preguntas sobre el uso de IA o reportar problemas:</p>
            <ul className="space-y-2 text-slate-600">
              <li><strong>Responsable:</strong> José Luis Vilaplana (@tecnoabogado)</li>
              <li><strong>Email:</strong> <a href="mailto:info@tecnoabogado.com" className="text-rose-600 hover:underline">info@tecnoabogado.com</a></li>
              <li><strong>Reporte IA:</strong> <a href="mailto:info@jurisciberia.com" className="text-rose-600 hover:underline">info@jurisciberia.com</a></li>
              <li><strong>Autoridad de control:</strong> Agencia Española de Protección de Datos (AEPD)</li>
            </ul>
          </section>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-slate-900 text-slate-400 py-8 px-6">
        <div className="max-w-4xl mx-auto text-center text-sm">
          <p>© 2026 vilaplana@tecnoabogado. Todos los derechos reservados.</p>
        </div>
      </footer>
    </div>
  );
}
